import { useEffect, useReducer } from "react"
import { Box, Flex, TableWithPagination, usePaginatedTable, Text } from "@shared/primitive-components"
import { useUI } from "../../../../contexts"
import { ActionButtons } from "../../../../components/ActionButtons"
import { manuallyResolveEntriesReducer } from "./manuallyResolveEntriesReducer"
import { getmanuallyResolvedEntriesInitialState } from "./utils"
import { AddedFleetEntry, ResolvedFleetEntry } from "../../../../api/fleet"
import { columns } from "./components/tableCells/columns"
import { ManuallyResolveEntryModal } from "./components/ManuallyResolveEntryModal"
import { TableEntry } from "./types"
import * as styles from "./styles"

export const MANUALLY_RESOLVE_ENTRIES_DTI = `manually_resolve_entries_root`

export const MANUALLY_RESOLVE_ENTRIES_BUTTON_DTI = {
  back: `manually_resolve_entries_back`,
  cancel: `manually_resolve_entries_cancel`,
  next: `manually_resolve_entries_next`,
}

export const MANUALLY_RESOLVE_ENTRIES_STATUSBAR_MESSAGES = {
  ERROR: `Some of your vehicles were not found by our vehicle lookup provider. You can resolve the problem by clicking on each row.`,
  INFO: `Please check that you are happy with the below vehicle makes and models.`,
}

export type ManuallyResolveEntriesProps = {
  dataTestId?: string
  entries: ReadonlyArray<AddedFleetEntry>
  onDone: (entries: ReadonlyArray<ResolvedFleetEntry>) => void
  onCancel: () => void
}

export const ManuallyResolveEntries = ({ dataTestId, entries, onDone, onCancel }: ManuallyResolveEntriesProps) => {
  const [state, dispatch] = useReducer(manuallyResolveEntriesReducer, getmanuallyResolvedEntriesInitialState(entries))
  const { setStatusBar } = useUI()
  useEffect(() => {
    setStatusBar({
      type: state.hasUnresolvedEntries ? `error` : `info`,
      message: state.hasUnresolvedEntries
        ? MANUALLY_RESOLVE_ENTRIES_STATUSBAR_MESSAGES.ERROR
        : MANUALLY_RESOLVE_ENTRIES_STATUSBAR_MESSAGES.INFO,
    })
    return () => {
      setStatusBar(undefined)
    }
  }, [state.hasUnresolvedEntries, setStatusBar])

  const onEditClose = () => {
    dispatch({ type: `setCurrentIndex`, index: undefined })
  }

  const onNext = () =>
    onDone(
      (state.entries as ReadonlyArray<Omit<TableEntry, "rowIndex"> & { rowIndex?: number }>).map((it) => {
        delete it.rowIndex
        return it
      }) as ResolvedFleetEntry[]
    )

  const { page, dataCount, pagination } = usePaginatedTable(state.entries, {
    doNotResetPaginationWhenDataChanges: true,
  })
  return (
    <Box data-testid={dataTestId} width="100%">
      <Text css={styles.errorText}>
        We could not automatically find all the required vehicle information for the vehicles you submitted. Please
        click on the vehicle(s) below to complete this information.
      </Text>
      <Flex width="100%" flexDirection="column" data-testid={MANUALLY_RESOLVE_ENTRIES_DTI}>
        <TableWithPagination<TableEntry>
          data={page}
          dataCount={dataCount}
          pagination={pagination}
          columns={columns}
          rowOnClickValueKey="rowIndex"
          rowOnClick={(value: unknown) => dispatch({ type: `setCurrentIndex`, index: value as number })}
        />
        <ActionButtons
          onCancel={onCancel}
          onNext={onNext}
          nextDisabled={state.hasUnresolvedEntries}
          dataTestIds={MANUALLY_RESOLVE_ENTRIES_BUTTON_DTI}
        />
      </Flex>
      {state.currentIndex !== undefined && (
        <ManuallyResolveEntryModal
          onClose={onEditClose}
          index={state.currentIndex + pagination.pageSize * pagination.pageIndex}
          entries={state.entries}
          dispatch={dispatch}
        />
      )}
    </Box>
  )
}

ManuallyResolveEntries.defaultProps = {
  dataTestId: undefined,
}
